import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import Logo from "../components/Logo";
import TopBar from "../components/TopBar";
import { trackPageViewAnalytics } from "../utils/visitorTracking";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";

import GuestlistIcon from "../assets/icons/Guestlist.png";
import CommunityDealsIcon from "../assets/icons/Loyalty.png";
import FindHousemateIcon from "../assets/icons/House.png";

const ChatCard = ({ chat, category }) => {
  const formattedName = chat.name
    .toLowerCase()
    .replace(/\(nl\)/g, '')
    .split(' ')
    .map(word => word === 'en' ? word : word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    // ! This cannot be a <Link /> because the urls are misformatted
    <a
      key={chat.id}
      href={chat.link}
      target="_blank"
      rel="noopener noreferrer"
      className="flex justify-start items-center gap-[16px] p-[20px] max-lg:px-[16px] max-lg:py-[12px] rounded-[12px] transition-all duration-300"
      style={{
        background: `radial-gradient(94.44% 100.01% at 51.51% 1.66%, rgba(194, 165, 255, 0.08) 0%, rgba(85, 75, 106, 0.04) 62.15%, rgba(0, 0, 0, 0.00) 100%), rgba(175, 147, 255, 0.06)`,
      }}
    >
      <div className="bg-[#7721E5] p-[8px] rounded-[8px]">
        <img
          src={category.icon}
          alt={chat.name}
          className="w-[24px] h-[24px]"
        />
      </div>
      <div className="flex flex-col">
        <div className="font-semibold text-[18px] text-white max-lg:text-[16px] tracking-[0px]">
          {category.text(formattedName)}
        </div>
        <p className="text-[16px] text-neutral-300 max-lg:text-[14px] leading-[22px] max-lg:leading-[20px] tracking-[0px]">
          {category.subText(formattedName)}
        </p>
      </div>
    </a>
  );
};

const CHAT_CATEGORIES = [
  {
    id: "guestlist_chat",
    label: "Guestlist chats",
    icon: GuestlistIcon,
    text: (city) => `Guestlist ${city}`,
    subText: (city) => `Our community chat for all events in ${city}`,
  },
  {
    id: "community_deals",
    label: "Community deals",
    icon: CommunityDealsIcon,
    text: (city) => `Community deals ${city}`,
    subText: (city) => `Our community chat for all deals in ${city}`,
  },
  {
    id: "find_housemate",
    label: "Find a house(mate)",
    icon: FindHousemateIcon,
    text: (city) => `Find a house(mate) ${city}`,
    subText: (city) => `Our community chat for all homes in ${city}`,
  },
];

const CommunityChats = ({ setLanguage }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = useSelector(selectUser);

  useEffect(() => {
    fetchChats();
  }, []);

  // Track page view analytics
  useEffect(() => {
    trackPageViewAnalytics('COMMUNITY_CHATS', user?.id || null);
  }, [user?.id]);

  const fetchChats = async () => {
    try {
      const { data, error } = await supabase
        .from("CommunityChat")
        .select("*")
        .eq("active", true)
        .order("index", { ascending: true });

      if (error) throw error;
      setChats(data);
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupChatsByCategory = (chats) => {
    const grouped = {};
    CHAT_CATEGORIES.forEach((category) => {
      grouped[category.id] = chats
        .filter((chat) => chat.category === category.id)
        // We don't need to sort here since the data is already sorted from the query
        // but we can add it as a safeguard
        .sort((a, b) => (a.index || 0) - (b.index || 0));
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center w-screen h-screen hero-section">
        <Logo />
      </div>
    );
  }

  const groupedChats = groupChatsByCategory(chats);

  return (
    <div className="flex justify-center w-full min-h-screen gradient-background">
      <div className="w-full max-w-[1200px]">
        <TopBar setLanguage={setLanguage} />
        <div className="px-[16px] pt-[80px]">
          <div className="pb-[40px] max-lg:pb-[24px] w-full font-semibold !text-[26px] text-white lg:!text-[32px] leading-[32px] lg:!leading-[36px] tracking-[0px]">
            Join our Whatsapp Communities
          </div>

          {CHAT_CATEGORIES.map((category) => {
            const categoryChats = groupedChats[category.id];
            if (!categoryChats || categoryChats.length === 0) return null;

            return (
              <section
                key={category.id}
                className="flex flex-col gap-[24px] max-lg:gap-[12px] pb-[40px] max-lg:pb-[24px]"
              >
                <h2 className="text-[26px] text-white max-lg:text-[22px] leading-[26px] tracking-[-0.5px]">
                  {category.label}
                </h2>
                <div className="gap-[20px] max-lg:gap-[12px] grid grid-cols-1 lg:grid-cols-2">
                  {categoryChats.map((chat) => (
                    <ChatCard key={chat.id} chat={chat} category={category} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CommunityChats;
